/**
 * CP 391 — "My phone alerts for this group": one of the camera box's four choices for every member camera the person
 * is a user of (Anwar: per camera AND per group; per account). Copy of GroupDefaultViewDialog (item 238).
 *
 * Anyone who can see the group opens it (owner, people it is shared with, superusers): the server writes only the
 * requester's own rows and skips member cameras they are not a user of. Every choice is first sent as a dry run and
 * the dialog shows its counts; Apply repeats the same call for real. A one-time action on today's members: a camera
 * added to the group later follows the person's account setting until they choose.
 *
 * It opens with NO choice selected: no dry run is sent and Apply stays disabled until the person picks one. A default
 * ("My account setting") would let one click on Apply remove every per-camera choice the person has in the group.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MyPushPicker } from '@/components/cameras/MyPushPicker'
import { cameraGroupsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { myPushRequest, type MyPushValue } from '@/lib/myPush'
import { toast } from '@/lib/toast'
import type { CameraGroup, MyPushChoice, MyPushMode, MyPushRequest } from '@/types'

/** The order of the "now" line: the choices, then a custom row this screen did not write. */
const NOW_ORDER: MyPushMode[] = ['account', 'all', 'categories', 'suspicious', 'critical_only', 'custom']

interface Props {
  group: CameraGroup
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GroupMyPushDialog({ group, open, onOpenChange }: Props) {
  const { t } = useTranslation('groups')
  const { t: ts } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const qc = useQueryClient()
  // null until the person picks a choice: nothing is previewed or applied before that
  const [value, setValue] = useState<(MyPushValue & { mode: MyPushChoice }) | null>(null)
  const body = value ? myPushRequest(value) : null

  // How many cameras are on each choice now (only while the dialog is open)
  const summary = useQuery({
    queryKey: queryKeys.cameraGroups.myPush(group.id),
    queryFn: ({ signal }) => cameraGroupsApi.getMyPush(group.id, signal),
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  const preview = useQuery({
    queryKey: queryKeys.cameraGroups.myPushPreview(group.id, body?.mode ?? '', body?.categories.join(',') ?? '', body?.suspicious),
    queryFn: body ? ({ signal }) => cameraGroupsApi.setMyPush(group.id, body, true, signal) : skipToken,
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  const apply = useMutation({
    mutationFn: (request: MyPushRequest) => cameraGroupsApi.setMyPush(group.id, request, false),
    onSuccess: (r) => {
      toast.success(r.skipped.length
        ? t('myPush.appliedSkipped', { count: r.updated.length, skipped: r.skipped.length })
        : t('myPush.applied', { count: r.updated.length }))
      // as GroupDefaultViewDialog: the camera lists, the groups, All Cameras (superusers) and its edit sheet —
      // ['cameras'] also covers each camera's "My phone alerts" box (['cameras', 'my-push', id])
      qc.invalidateQueries({ queryKey: ['cameras'] })
      qc.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      qc.invalidateQueries({ queryKey: ['all-cameras'] })
      qc.invalidateQueries({ queryKey: ['camera'] })
      // the bell (and PushDeadZones) apply the stored filters of these rows
      qc.invalidateQueries({ queryKey: queryKeys.pushPreferences.cameras() })
      onOpenChange(false)
    },
    onError: () => toast.error(t('myPush.failed')),
  })

  const counts = preview.data
  const canApply = !!body && !!counts && !preview.isFetching && counts.updated.length > 0 && !apply.isPending
  const now = NOW_ORDER
    .map((mode) => ({ mode, count: summary.data?.counts?.[mode] ?? 0 }))
    .filter((c) => c.count > 0)

  return (
    <Dialog.Root open={open} onOpenChange={apply.isPending ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Dialog.Title className="text-lg font-semibold">
                {t('myPush.title', { name: group.name })}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">{t('myPush.description')}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label={tc('actions.close')} disabled={apply.isPending}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {now.length > 0 && (
            <p data-testid="my-push-now" className="mt-3 text-xs text-muted-foreground">
              {t('myPush.now', {
                summary: now
                  .map((c) => t('myPush.nowPart', { count: c.count, mode: ts(`myPush.modes.${c.mode}`) }))
                  .join(ts('myPush.listSeparator')),   // ", " in English, "、" in Japanese
              })}
            </p>
          )}

          <div className="mt-4">
            <MyPushPicker
              idPrefix={`group-my-push-${group.id}`}
              value={value}
              onChange={setValue}
              disabled={apply.isPending}
              label={t('myPush.button')}
            />
          </div>

          <div className="mt-4 min-h-[4.5rem] rounded-lg bg-muted/50 p-3 text-sm" aria-live="polite">
            {!body ? (
              <p className="text-muted-foreground">{t('myPush.pickFirst')}</p>
            ) : preview.isError ? (
              <p className="text-destructive">{t('myPush.previewFailed')}</p>
            ) : !counts || preview.isFetching ? (
              <p className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />{t('myPush.checking')}
              </p>
            ) : (
              <ul className="space-y-0.5">
                <li className="font-medium">{t('myPush.willChange', { count: counts.updated.length })}</li>
                {counts.unchanged.length > 0 && (
                  <li className="text-muted-foreground">{t('myPush.alreadySet', { count: counts.unchanged.length })}</li>
                )}
                {counts.skipped.length > 0 && (
                  <li className="text-muted-foreground">{t('myPush.notUser', { count: counts.skipped.length })}</li>
                )}
              </ul>
            )}
          </div>

          <div className="mt-6 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={apply.isPending}>
              {tc('actions.cancel')}
            </Button>
            <Button className="flex-1" onClick={() => { if (body) apply.mutate(body) }} disabled={!canApply}>
              {apply.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('myPush.apply')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
